import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin,
  Monitor,
  Wifi,
  WifiOff,
  AlertCircle,
  RefreshCw,
  Search,
  Battery,
  Clock,
  Info,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { getPosLocations, type PosLocationItem, type PosLocationsResponse } from "@/lib/api";
import { formatRelative } from "@/lib/format";

// Fix default marker icons for Leaflet + Vite
type LeafletIconWithGetter = typeof L.Icon.Default.prototype & { _getIconUrl?: unknown };
delete (L.Icon.Default.prototype as LeafletIconWithGetter)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const greenIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const orangeIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export function PosLocationsPage() {
  const auth = useAuth();
  const merchantId = auth.status === "authenticated" ? auth.user.merchant_id : null;

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});

  const query = useQuery<PosLocationsResponse>({
    queryKey: ["pos-locations", merchantId],
    queryFn: getPosLocations,
    enabled: !!merchantId,
  });

  const data = query.data ?? null;
  const total = data?.counts.total ?? 0;
  const withGps = data?.counts.with_gps ?? 0;
  const stale = data?.counts.stale ?? 0;
  const never = total - withGps;
  const noGpsYet = !!data && total > 0 && withGps === 0;

  const filtered = useMemo(() => {
    if (!data) return [] as PosLocationItem[];
    const q = search.trim().toLowerCase();
    if (!q) return data.pos;
    return data.pos.filter(
      (p) =>
        p.pos_id.toLowerCase().includes(q) ||
        (p.store_name || "").toLowerCase().includes(q) ||
        (p.current_user?.firstname || "").toLowerCase().includes(q) ||
        (p.current_user?.lastname || "").toLowerCase().includes(q),
    );
  }, [data, search]);

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current || query.isLoading) return;
    const map = L.map(containerRef.current, {
      center: [-4.33, 23.0], // Center of DRC
      zoom: 5,
      zoomControl: true,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, [query.isLoading]);

  // Render markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};

    const bounds = L.latLngBounds([]);
    data.pos.forEach((p) => {
      if (!p.live || p.live.latitude == null || p.live.longitude == null) return;
      const icon = p.live.is_stale ? orangeIcon : greenIcon;
      const marker = L.marker([p.live.latitude, p.live.longitude], { icon }).addTo(map);
      const cashier = p.current_user
        ? `${p.current_user.firstname || ""} ${p.current_user.lastname || ""}`.trim() || "—"
        : "—";
      marker.bindPopup(
        `<div style="font-family:Inter,sans-serif;font-size:12px;">
          <div style="font-weight:700;color:#181c32;">${escapeHtml(p.store_name) || "POS"}</div>
          <div style="font-family:ui-monospace,monospace;color:#7e8299;font-size:11px;">${escapeHtml(p.pos_id)}</div>
          <div style="margin-top:4px;color:#3f4254;">Caissier : ${escapeHtml(cashier)}</div>
          <div style="color:${p.live.is_stale ? "#d97706" : "#059669"};font-weight:600;font-size:11px;margin-top:4px;">
            ${p.live.is_stale ? "Position obsolète" : "Position récente"} · ${p.live.received_at ? escapeHtml(formatRelative(p.live.received_at)) : "jamais"}
          </div>
        </div>`,
      );
      markersRef.current[p.pos_id] = marker;
      bounds.extend([p.live.latitude, p.live.longitude]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [data]);

  // Pan on row selection
  useEffect(() => {
    if (!selected || !mapRef.current) return;
    const m = markersRef.current[selected];
    if (m) {
      mapRef.current.flyTo(m.getLatLng(), 14, { duration: 0.8 });
      m.openPopup();
    }
  }, [selected]);

  return (
    <div>
      <PageHeader
        title="Localisation des POS"
        breadcrumbs={[{ label: "Mon commerce" }, { label: "Localisation POS" }]}
        actions={
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="btn btn-light btn-sm"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
            Actualiser
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard
          label="Total POS"
          value={total.toLocaleString("fr-FR")}
          sub="terminaux enregistrés"
          icon={<Monitor size={20} />}
          color="primary"
        />
        <StatCard
          label="Avec GPS"
          value={withGps.toLocaleString("fr-FR")}
          sub={total ? `${Math.round((withGps / total) * 100)}% du parc` : "—"}
          icon={<Wifi size={20} />}
          color="success"
        />
        <StatCard
          label="Position obsolète"
          value={stale.toLocaleString("fr-FR")}
          sub=">1h sans heartbeat"
          icon={<AlertCircle size={20} />}
          color="warning"
        />
        <StatCard
          label="Jamais vu"
          value={never.toLocaleString("fr-FR")}
          sub="GPS pas encore activé"
          icon={<WifiOff size={20} />}
          color="info"
        />
      </div>

      {noGpsYet && (
        <div className="mb-4 rounded-lg border border-warning-100 bg-warning-50 p-3 flex items-start gap-2">
          <Info size={14} className="text-warning-700 shrink-0 mt-0.5" />
          <div className="text-xs text-warning-700">
            <strong>Aucun de vos POS n'envoie encore sa position GPS.</strong> L'app installée sur
            les terminaux doit envoyer ses coordonnées au serveur. Demandez à votre installateur
            d'activer le module GPS.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Map */}
        <div className="xl:col-span-2 card overflow-hidden">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-brand-600" />
              <div>
                <div className="card-title">Carte du parc</div>
                <div className="card-subtitle flex items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-success-500" /> Récent
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-warning-500" /> Obsolète
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div ref={containerRef} className="w-full" style={{ height: 480 }} />
        </div>

        {/* List */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Terminaux</div>
              <div className="card-subtitle">
                {filtered.length} sur {total}
              </div>
            </div>
          </div>
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="POS ID, magasin, caissier…"
                className="form-input pl-9 py-2 text-sm w-full"
              />
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
            {query.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Aucun POS trouvé.</div>
            ) : (
              filtered.map((p) => {
                const active = selected === p.pos_id;
                const hasGps = p.live?.latitude != null && p.live?.longitude != null;
                return (
                  <button
                    key={p.pos_id}
                    type="button"
                    onClick={() => setSelected(p.pos_id)}
                    disabled={!hasGps}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                      active ? "bg-brand-50" : "hover:bg-slate-50"
                    } ${!hasGps ? "opacity-60" : ""}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        hasGps
                          ? p.live?.is_stale
                            ? "bg-warning-50 text-warning-600"
                            : "bg-success-50 text-success-600"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {hasGps ? <MapPin size={14} /> : <WifiOff size={14} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-[#181c32] truncate">
                        {p.store_name || "Sans magasin"}
                      </div>
                      <div className="font-mono text-[10px] text-slate-500 truncate">
                        {p.pos_id}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {p.current_user
                          ? `${p.current_user.firstname || ""} ${p.current_user.lastname || ""}`.trim() ||
                            "Sans caissier"
                          : "Sans caissier"}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        {hasGps ? (
                          <>
                            <span className="inline-flex items-center gap-1">
                              <Clock size={10} />
                              {p.live?.received_at ? formatRelative(p.live.received_at) : "—"}
                            </span>
                            {p.live?.battery_level != null && (
                              <span className="inline-flex items-center gap-1">
                                <Battery size={10} />
                                {p.live.battery_level}%
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="italic">Position jamais reçue</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  sub,
  icon,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "info" | "warning";
}) {
  const cls: Record<typeof color, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
    warning: "bg-warning-50 text-warning-700",
  };
  return (
    <div className="card card-body">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
          <div className="text-2xl font-bold text-[#181c32] mt-2 truncate">{value}</div>
          {sub && <div className="text-xs text-[#7e8299] mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
